import axios from 'axios';
import adminApi from './adminApi';
import { config } from '../config/environment';
import { SystemAdminDTO } from './systemAdminService';

/**
 * Core returns board-service baseUrl from server config. If production still has
 * CURRICULUM_SERVICE_URL unset, it defaults to http://localhost:8082 — the browser
 * then calls the user's own machine and shows "Network Error". When the API returns
 * a loopback URL, prefer the public Scottish service URL from the frontend build.
 */
function boardServiceBaseUrlForBrowser(serverProvidedBaseUrl: string): string {
  const trimmed = serverProvidedBaseUrl.replace(/\/$/, '');
  if (!isLoopbackUrl(trimmed)) {
    return trimmed;
  }
  if (!import.meta.env.PROD) {
    return trimmed;
  }
  const fromBuild = (config.scottishCurriculumApiUrl || '').replace(/\/$/, '');
  if (!fromBuild || isLoopbackUrl(fromBuild)) {
    return trimmed;
  }
  return fromBuild;
}

function isLoopbackUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
  } catch {
    return /localhost|127\.0\.0\.1/.test(url);
  }
}

// ========== DTOs ==========
export interface CourseDTO {
  id?: number;
  name: string;
  description?: string;
  courseType?: string;
  status?: string;
  /** Enrollment registration method: OPEN | CODE | INVITE */
  registrationMethod?: string;
  maxStudents?: number;
  durationWeeks?: number;
  difficultyLevel?: string;
  prerequisites?: string;
  curriculumAlignment?: string;
  curriculumSubjectId?: number;
  curriculumId?: number;
  board?: string;
  /** JUNIOR | SENIOR when courseType is ABACUS_MATHS */
  abacusVariant?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  // Enriched display fields (populated client-side)
  curriculumSubjectName?: string;
  coreSubjectName?: string;
  boardName?: string;
  coreCurriculumVersion?: string;
  scottishCurriculumId?: number;
  scottishLevel?: string;
}

export interface CurriculumDTO {
  id: number;
  countryId: number;
  countryName?: string;
  boardId: number;
  boardName?: string;
  curriculumSubjectId: number;
  subjectName?: string;
  version?: string;
  level?: string;
  description?: string;
}

export interface CountryDTO {
  id: number;
  name: string;
  code: string;
}

/**
 * Get all available countries
 */
export const getAllCountries = async (): Promise<CountryDTO[]> => {
  const response = await adminApi.get('/api/admin/courses/countries');
  return response.data.data;
};

/**
 * Get curriculums filtered by subject and/or country
 */
export const getCurriculums = async (curriculumSubjectId?: number, countryId?: number): Promise<CurriculumDTO[]> => {
  let url = '/api/admin/courses/curriculums';
  const params = new URLSearchParams();
  if (curriculumSubjectId) params.append('curriculumSubjectId', curriculumSubjectId.toString());
  if (countryId) params.append('countryId', countryId.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await adminApi.get(url);
  return response.data.data;
};

export const createCurriculum = async (curriculum: CurriculumDTO): Promise<CurriculumDTO> => {
  const response = await adminApi.post('/api/admin/courses/curriculums', curriculum);
  return response.data.data;
};

export const updateCurriculum = async (id: number, curriculum: CurriculumDTO): Promise<CurriculumDTO> => {
  const response = await adminApi.put(`/api/admin/courses/curriculums/${id}`, curriculum);
  return response.data.data;
};

export const getCurriculumsBySubject = getCurriculums; // Alias for backward compatibility if needed

export interface CoreTopicDTO {
  id: number;
  title: string;
  description?: string;
  topicCode?: string;
  parentTopicId?: number | null;
  children?: CoreTopicDTO[];
  sortOrder?: number;
  isActive?: boolean;
  content?: string;
}

// ========== API Functions ==========

/**
 * Get all courses (filtered by admin access)
 */
export const getAllCourses = async (): Promise<CourseDTO[]> => {
  const response = await adminApi.get('/api/admin/courses');
  return response.data.data;
};

/**
 * Get course by ID
 */
export const getCourseById = async (courseId: number): Promise<CourseDTO> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}`);
  return response.data.data;
};

/**
 * Create a new course
 */
export const createCourse = async (
  course: CourseDTO
): Promise<CourseDTO> => {
  const response = await adminApi.post('/api/admin/courses', course);
  return response.data.data;
};

/**
 * Update a course
 */
export const updateCourse = async (
  courseId: number,
  course: CourseDTO
): Promise<CourseDTO> => {
  const response = await adminApi.put(`/api/admin/courses/${courseId}`, course);
  return response.data.data;
};

/**
 * Deactivate a course
 */
export const deactivateCourse = async (courseId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/courses/${courseId}`);
};

/**
 * Get courses assigned to current admin
 */
export const getMyCourses = async (): Promise<CourseDTO[]> => {
  const response = await adminApi.get('/api/admin/courses/my-courses');
  return response.data.data;
};

/**
 * Get all curriculum subjects (for course/topic management)
 */
/**
 * Get core (universal) subjects e.g. Mathematics, Science.
 */
export const getCoreSubjects = async (): Promise<
  { id: number; code: string; name: string; description?: string }[]
> => {
  const response = await adminApi.get('/api/admin/courses/core-subjects');
  return response.data.data;
};

/**
 * Get assigned admins for a core subject (Subjects & Topics page).
 *
 * Use this instead of resolving via curriculum_subject — core-subject admins
 * are a deliberately separate population in the post-011 model.
 */
export const getCoreSubjectAdmins = async (
  coreSubjectId: number
): Promise<SystemAdminDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/core-subjects/${coreSubjectId}/admins`);
  return response.data.data;
};

/**
 * Assign system admins to a core subject (Super Admin only).
 */
export const assignAdminsToCoreSubject = async (
  coreSubjectId: number,
  adminIds: number[]
): Promise<void> => {
  await adminApi.post(`/api/admin/courses/core-subjects/${coreSubjectId}/admins`, {
    adminIds,
  });
};

/**
 * Remove a single admin from a core subject (sets the assignment to INACTIVE).
 */
export const removeAdminFromCoreSubject = async (
  coreSubjectId: number,
  adminId: number
): Promise<void> => {
  await adminApi.delete(`/api/admin/courses/core-subjects/${coreSubjectId}/admins/${adminId}`);
};

/**
 * Create a core subject (Subjects & Topics).
 */
export const createCoreSubject = async (
  subject: { code: string; name: string; description?: string }
): Promise<{ id: number; code: string; name: string; description?: string }> => {
  const response = await adminApi.post('/api/admin/courses/core-subjects', subject);
  return response.data.data;
};

/**
 * Update a core subject.
 */
export const updateCoreSubject = async (
  id: number,
  subject: { code: string; name: string; description?: string }
): Promise<{ id: number; code: string; name: string; description?: string }> => {
  const response = await adminApi.put(`/api/admin/courses/core-subjects/${id}`, subject);
  return response.data.data;
};

/**
 * Delete a core subject (and linked curriculum subject(s) in core).
 */
export const deleteCoreSubject = async (id: number): Promise<void> => {
  await adminApi.delete(`/api/admin/courses/core-subjects/${id}`);
};

export const getCurriculumSubjects = async (): Promise<
  { id: number; code: string; name: string; description?: string; coreSubjectId?: number; boardId?: number }[]
> => {
  const response = await adminApi.get('/api/admin/courses/curriculum-subjects');
  return response.data.data;
};

/**
 * Get a curriculum subject by ID
 */
export const getCurriculumSubjectById = async (
  subjectId: number
): Promise<{ id: number; code: string; name: string; description?: string; coreSubjectId?: number; boardId?: number }> => {
  const response = await adminApi.get(`/api/admin/courses/curriculum-subjects/${subjectId}`);
  return response.data.data;
};

/**
 * Create a curriculum subject in core (linked to a core subject and board).
 */
export const createCurriculumSubject = async (
  subject: { coreSubjectId?: number; boardId?: number; code: string; name: string; description?: string }
): Promise<{ id: number; code: string; name: string; description?: string; coreSubjectId?: number; boardId?: number }> => {
  const response = await adminApi.post('/api/admin/courses/curriculum-subjects', subject);
  return response.data.data;
};

/**
 * Update a curriculum subject
 */
export const updateCurriculumSubject = async (
  subjectId: number,
  subject: { coreSubjectId?: number; boardId?: number; code: string; name: string; description?: string }
): Promise<{ id: number; code: string; name: string; description?: string; coreSubjectId?: number; boardId?: number }> => {
  const response = await adminApi.put(`/api/admin/courses/curriculum-subjects/${subjectId}`, subject);
  return response.data.data;
};

/**
 * Delete a curriculum subject
 */
export const deleteCurriculumSubject = async (
  subjectId: number
): Promise<void> => {
  await adminApi.delete(`/api/admin/courses/curriculum-subjects/${subjectId}`);
};

/**
 * Get assigned admins for a curriculum subject
 */
export const getSubjectAdmins = async (subjectId: number): Promise<SystemAdminDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/curriculum-subjects/${subjectId}/admins`);
  return response.data.data;
};

/**
 * Assign system admins to a curriculum subject
 */
export const assignAdminsToSubject = async (
  subjectId: number,
  adminIds: number[]
): Promise<void> => {
  await adminApi.post(`/api/admin/courses/curriculum-subjects/${subjectId}/assign-admins`, {
    adminIds,
  });
};

/**
 * Get core topics by curriculum subject ID (core topic library)
 * Now uses Neo4j graph database endpoint
 */
export const getCoreTopicsByCurriculumSubject = async (
  curriculumSubjectId: number
): Promise<CoreTopicDTO[]> => {
  const response = await adminApi.get(
    `/api/admin/graph/topics/subject/${curriculumSubjectId}/tree`
  );
  return response.data.data;
};

/**
 * Create a new Core Topic (Graph Mode)
 */
export const createCoreTopic = async (
  subjectId: number,
  topic: { title: string; description?: string; parentTopicId?: number; displayOrder?: number }
): Promise<CoreTopicDTO> => {
  const response = await adminApi.post(`/api/admin/graph/topics/subject/${subjectId}`, topic);
  return response.data.data;
};

/**
 * Update a Core Topic (Graph Mode)
 */
export const updateCoreTopic = async (
  topicId: number,
  topic: { title: string; description?: string; parentTopicId?: number; displayOrder?: number }
): Promise<CoreTopicDTO> => {
  const response = await adminApi.put(`/api/admin/graph/topics/${topicId}`, topic);
  return response.data.data;
};

/**
 * Delete a Core Topic (Graph Mode)
 */
export const deleteCoreTopic = async (topicId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/graph/topics/${topicId}`);
};

/**
 * Get a Core Topic by ID (Graph Mode)
 */
export const getCoreTopicById = async (topicId: number): Promise<CoreTopicDTO> => {
  const response = await adminApi.get(`/api/admin/graph/topics/${topicId}`);
  return response.data.data;
};

/**
 * Get EO hierarchy by curriculum subject ID
 */
export interface EOHierarchyDTO {
  areaId: number;
  areaName: string;
  areaDescription?: string;
  areaDisplayOrder?: number;
  groups: GroupDTO[];
}

export interface GroupDTO {
  groupId: number;
  groupName: string;
  groupDescription?: string;
  groupDisplayOrder?: number;
  experienceOutcomes: ExperienceOutcomeDTO[];
}

export interface ExperienceOutcomeDTO {
  eoId: number;
  code: string;
  description: string;
  level?: string;
  displayOrder?: number;
}

/** Board service info returned by core for a curriculum. */
export interface BoardServiceInfoDTO {
  boardType: string;
  baseUrl: string;
  hierarchyPathTemplate: string;
  managementPathPrefix: string;
}

/** Result of fetching curriculum structure from the board service (includes Scottish context for CRUD). */
export interface CurriculumStructureBoardData {
  hierarchy: EOHierarchyDTO[];
  scottishSubjectId?: number;
  boardServiceBaseUrl?: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SCOTTISH_MANAGEMENT_PREFIX = '/api/scottish-curriculum/management';

/**
 * Resolve Scottish subject by curriculum subject id, creating the board link if missing.
 * Mirrors the Curriculum Subjects tab save flow (createSubject when scottishId absent).
 */
export const ensureScottishSubjectLink = async (
  baseUrl: string,
  curriculumSubjectId: number
): Promise<{ id: number; curriculumSubjectId?: number }> => {
  const normalizedBase = baseUrl.replace(/\/$/, '');
  const authHeader = getAuthHeaders();
  const lookupUrl = `${normalizedBase}/api/scottish-curriculum/subjects/by-curriculum-subject/${curriculumSubjectId}`;

  const fetchLinked = () =>
    axios.get<{ id: number; curriculumSubjectId?: number }>(lookupUrl, {
      headers: authHeader,
      timeout: 15000,
    });

  try {
    const res = await fetchLinked();
    return res.data;
  } catch (err: any) {
    if (err.response?.status !== 404) throw err;
  }

  try {
    const created = await axios.post<{ success: boolean; data: { id: number; curriculumSubjectId?: number } }>(
      `${normalizedBase}${SCOTTISH_MANAGEMENT_PREFIX}/subjects`,
      { curriculumSubjectId },
      { headers: authHeader, timeout: 15000 }
    );
    const subject = created.data?.data;
    if (subject?.id != null) return subject;
  } catch (createErr: any) {
    if (createErr.response?.status !== 400) throw createErr;
    // Already linked (concurrent create or stale 404) — retry lookup
  }

  const retry = await fetchLinked();
  return retry.data;
};

/**
 * Fetch curriculum hierarchy from the board-specific service (e.g. Scottish).
 * Returns hierarchy plus Scottish subject id and base URL for use in CRUD on the structure page.
 */
export const getCurriculumHierarchyFromBoardService = async (
  curriculumId: number,
  /** When set (e.g. from the course), hierarchy is loaded for this subject — not the curriculum record's default subject. */
  curriculumSubjectIdOverride?: number
): Promise<CurriculumStructureBoardData> => {
  const curriculumRes = await adminApi.get<{ success: boolean; data: { curriculumSubjectId: number; id: number } }>(
    `/api/admin/courses/curriculums/${curriculumId}`
  );
  if (!curriculumRes.data.success || !curriculumRes.data.data) {
    throw new Error((curriculumRes.data as any)?.message || 'Curriculum not found');
  }
  const curriculum = curriculumRes.data.data;
  const curriculumSubjectId = curriculumSubjectIdOverride ?? curriculum.curriculumSubjectId;
  if (curriculumSubjectId == null) {
    throw new Error(
      'This curriculum has no curriculum subject linked. Edit the curriculum and assign a curriculum subject before managing structure.'
    );
  }

  const serviceInfoRes = await adminApi.get<{ success: boolean; data: BoardServiceInfoDTO }>(
    `/api/curriculum/curricula/${curriculumId}/service-info`
  );
  if (!serviceInfoRes.data.success || !serviceInfoRes.data.data) {
    throw new Error(serviceInfoRes.data?.message || 'Board service not configured for this curriculum');
  }
  const serviceInfo = serviceInfoRes.data.data;
  const baseUrl = boardServiceBaseUrlForBrowser(serviceInfo.baseUrl).replace(/\/$/, '');
  const authHeader = getAuthHeaders();

  if (serviceInfo.boardType?.toUpperCase() === 'SCOTTISH') {
    const scottishSubject = await ensureScottishSubjectLink(baseUrl, curriculumSubjectId);
    const hierarchyRes = await axios.get<{ areas: Array<{
      id: number;
      name: string;
      description?: string;
      displayOrder?: number;
      groups: Array<{
        id: number;
        name: string;
        description?: string;
        displayOrder?: number;
        experienceOutcomes: Array<{ id: number; code: string; description: string; level?: string; displayOrder?: number }>;
      }>;
    }> }>(`${baseUrl}/api/scottish-curriculum/subjects/${scottishSubject.id}/hierarchy`, {
      headers: authHeader,
      timeout: 15000,
    });
    const dto = hierarchyRes.data;
    const hierarchy: EOHierarchyDTO[] = (dto.areas || []).map((area) => ({
      areaId: area.id,
      areaName: area.name,
      areaDescription: area.description,
      areaDisplayOrder: area.displayOrder,
      groups: (area.groups || []).map((g) => ({
        groupId: g.id,
        groupName: g.name,
        groupDescription: g.description,
        groupDisplayOrder: g.displayOrder,
        experienceOutcomes: (g.experienceOutcomes || []).map((eo) => ({
          eoId: eo.id,
          code: eo.code,
          description: eo.description,
          level: eo.level,
          displayOrder: eo.displayOrder,
        })),
      })),
    }));
    return { hierarchy, scottishSubjectId: scottishSubject.id, boardServiceBaseUrl: baseUrl };
  }

  throw new Error(`Board type "${serviceInfo.boardType}" is not supported for hierarchy`);
};

/**
 * Get EO hierarchy by curriculum ID.
 * Uses board-specific curriculum service (e.g. Scottish) via core service-info.
 */
export const getEOHierarchyByCurriculum = async (
  curriculumId: number,
  curriculumSubjectId?: number
): Promise<EOHierarchyDTO[]> => {
  const data = await getCurriculumHierarchyFromBoardService(curriculumId, curriculumSubjectId);
  return data.hierarchy;
};

const MANAGEMENT_PREFIX = SCOTTISH_MANAGEMENT_PREFIX;

/**
 * Scottish curriculum management API – all calls go to Scottish service, not core.
 * Use boardServiceBaseUrl from getCurriculumHierarchyFromBoardService.
 */
export const scottishCurriculumManagementApi = {
  createArea: (baseUrl: string, subjectId: number, body: { name: string; description?: string; displayOrder?: number }) =>
    axios.post(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/subjects/${subjectId}/areas`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  updateArea: (baseUrl: string, areaId: number, body: { name: string; description?: string; displayOrder?: number }) =>
    axios.put(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/areas/${areaId}`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  deleteArea: (baseUrl: string, areaId: number) =>
    axios.delete(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/areas/${areaId}`, { headers: getAuthHeaders(), timeout: 15000 }),

  createGroup: (baseUrl: string, areaId: number, body: { name: string; description?: string; displayOrder?: number }) =>
    axios.post(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/areas/${areaId}/groups`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  updateGroup: (baseUrl: string, groupId: number, body: { name: string; description?: string; displayOrder?: number }) =>
    axios.put(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/groups/${groupId}`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  deleteGroup: (baseUrl: string, groupId: number) =>
    axios.delete(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/groups/${groupId}`, { headers: getAuthHeaders(), timeout: 15000 }),

  createOutcome: (baseUrl: string, groupId: number, body: { code: string; description?: string; level?: string; displayOrder?: number }) =>
    axios.post(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/groups/${groupId}/outcomes`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  updateOutcome: (baseUrl: string, eoId: number, body: { code: string; description?: string; level?: string; displayOrder?: number }) =>
    axios.put(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/outcomes/${eoId}`, body, { headers: getAuthHeaders(), timeout: 15000 }),

  deleteOutcome: (baseUrl: string, eoId: number) =>
    axios.delete(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/outcomes/${eoId}`, { headers: getAuthHeaders(), timeout: 15000 }),

  // Curriculum subjects (Scottish link to core curriculum_subject; only carries scottish ID + FK)
  listSubjects: (baseUrl: string) =>
    axios.get<Array<{ id: number; curriculumSubjectId?: number }>>(
      `${baseUrl.replace(/\/$/, '')}/api/scottish-curriculum/subjects`,
      { headers: getAuthHeaders(), timeout: 15000 }
    ),
  createSubject: (baseUrl: string, body: { curriculumSubjectId: number }) =>
    axios.post(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/subjects`, body, { headers: getAuthHeaders(), timeout: 15000 }),
  updateSubject: (baseUrl: string, subjectId: number, body: { curriculumSubjectId: number }) =>
    axios.put(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/subjects/${subjectId}`, body, { headers: getAuthHeaders(), timeout: 15000 }),
  deleteSubject: (baseUrl: string, subjectId: number) =>
    axios.delete(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/subjects/${subjectId}`, { headers: getAuthHeaders(), timeout: 15000 }),

  // Scottish curricula (extends core_curricula; stores CfE level)
  createCurriculum: (baseUrl: string, body: { curriculumId: number; level: string }) =>
    axios.post(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/curricula`, body, { headers: getAuthHeaders(), timeout: 15000 }),
  updateCurriculum: (baseUrl: string, id: number, body: { curriculumId: number; level: string }) =>
    axios.put(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/curricula/${id}`, body, { headers: getAuthHeaders(), timeout: 15000 }),
  getCurriculumByCoreId: (baseUrl: string, curriculumId: number) =>
    axios.get<{ success: boolean; data: { id: number; curriculumId: number; level: string } }>(
      `${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/curricula?curriculumId=${curriculumId}`,
      { headers: getAuthHeaders(), timeout: 15000 }
    ),
  deleteCurriculum: (baseUrl: string, id: number) =>
    axios.delete(`${baseUrl.replace(/\/$/, '')}${MANAGEMENT_PREFIX}/curricula/${id}`, { headers: getAuthHeaders(), timeout: 15000 }),

  getSubjects: (baseUrl: string) =>
    axios.get<Array<{ id: number; curriculumSubjectId?: number }>>(
      `${baseUrl.replace(/\/$/, '')}/api/scottish-curriculum/subjects`,
      { headers: getAuthHeaders(), timeout: 15000 }
    ),
};

/**
 * Add topic(s) to a course
 */
export interface AddTopicToCourseRequest {
  topicId: number;
  displayOrder?: number;
  includeChildren?: boolean;
}

export const addTopicToCourse = async (
  courseId: number,
  request: AddTopicToCourseRequest
): Promise<void> => {
  await adminApi.post(`/api/admin/courses/${courseId}/course-topics`, request);
};

/**
 * Get admins assigned to a course (super admin only)
 */
export const getAdminsForCourse = async (
  courseId: number
): Promise<SystemAdminDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}/admins`);
  return response.data.data;
};

// ========== Book DTOs and APIs ==========
export interface BookDTO {
  id?: number;
  title: string;
  publisher: string;
  authors?: string;
  isbn?: string;
  edition?: string;
  year?: number;
  coverImageUrl?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const getAllBooks = async (): Promise<BookDTO[]> => {
  const response = await adminApi.get('/api/admin/books');
  return response.data.data;
};

export const getBookById = async (bookId: number): Promise<BookDTO> => {
  const response = await adminApi.get(`/api/admin/books/${bookId}`);
  return response.data.data;
};

export const createBook = async (book: BookDTO): Promise<BookDTO> => {
  const response = await adminApi.post('/api/admin/books', book);
  return response.data.data;
};

export const updateBook = async (bookId: number, book: Partial<BookDTO>): Promise<BookDTO> => {
  const response = await adminApi.put(`/api/admin/books/${bookId}`, book);
  return response.data.data;
};

export const deleteBook = async (bookId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/books/${bookId}`);
};

// ========== Course View DTOs and APIs ==========
export interface CourseViewDTO {
  id?: number;
  courseId: number;
  name: string;
  description?: string;
  viewType: 'DEFAULT' | 'BOOK_BASED';
  bookId?: number;
  bookName?: string;
  version?: string;
  isActive?: boolean;
  isDefault?: boolean;
  activeVersionId?: number | null;
  topicCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getViewsByCourse = async (courseId: number): Promise<CourseViewDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}/views`);
  return response.data.data;
};

export const getDistinctViews = async (courseId: number): Promise<number[]> => {
  const response = await adminApi.get(`/api/admin/graph/courses/${courseId}/views`);
  return response.data;
};

export const getBookViewsByCourse = async (courseId: number): Promise<CourseViewDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}/views/book-based`);
  return response.data.data;
};

export const getViewById = async (courseId: number, viewId: number): Promise<CourseViewDTO> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}/views/${viewId}`);
  return response.data.data;
};

export const createView = async (courseId: number, view: CourseViewDTO): Promise<CourseViewDTO> => {
  const response = await adminApi.post(`/api/admin/courses/${courseId}/views`, view);
  return response.data.data;
};

export const updateView = async (courseId: number, viewId: number, view: Partial<CourseViewDTO>): Promise<CourseViewDTO> => {
  const response = await adminApi.put(`/api/admin/courses/${courseId}/views/${viewId}`, view);
  return response.data.data;
};

export const deleteView = async (courseId: number, viewId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/courses/${courseId}/views/${viewId}`);
};

/** Set which published version is active for this view (what students see). Pass null to clear. */
export const setActiveVersion = async (
  courseId: number,
  viewId: number,
  versionId: number | null
): Promise<CourseViewDTO> => {
  const response = await adminApi.put(
    `/api/admin/courses/${courseId}/views/${viewId}/active-version`,
    { versionId }
  );
  return response.data.data;
};

// ========== Quiz & Assessment DTOs and APIs ==========

export interface QuizDTO {
  id?: number;
  title: string;
  description?: string;
  topicId: number;
  courseId: number;
  totalQuestions?: number;
  timeLimitMinutes?: number;
  passingScore?: number;
  maxAttempts?: number;
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  type?: 'QUIZ' | 'ASSESSMENT';
  questions?: QuizQuestionDTO[];
}

export interface QuizQuestionDTO {
  id?: number;
  questionText: string;
  questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'SHORT_ANSWER';
  correctAnswer: string;
  options?: string; // JSON string
  points?: number;
  displayOrder?: number;
}

export interface TopicAssessmentRuleDTO {
  id?: number;
  topicId: number;
  title: string;
  type?: 'QUIZ' | 'ASSESSMENT';
  difficultyLevel: 'EASY' | 'MEDIUM' | 'HARD' | 'ADAPTIVE';
  questionCount: number;
  promptTemplate?: string;
  isActive?: boolean;
}

export const getQuizzesByTopic = async (topicId: number, type?: 'QUIZ' | 'ASSESSMENT'): Promise<QuizDTO[]> => {
  const url = `/api/admin/quizzes/topic/${topicId}${type ? `?type=${type}` : ''}`;
  const response = await adminApi.get(url);
  return response.data.data;
};

export const createQuiz = async (quiz: QuizDTO): Promise<QuizDTO> => {
  const response = await adminApi.post('/api/admin/quizzes', quiz);
  return response.data.data;
};

export const updateQuiz = async (quizId: number, quiz: QuizDTO): Promise<QuizDTO> => {
  const response = await adminApi.put(`/api/admin/quizzes/${quizId}`, quiz);
  return response.data.data;
};

export const deleteQuiz = async (quizId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/quizzes/${quizId}`);
};

export const getRulesByTopic = async (topicId: number): Promise<TopicAssessmentRuleDTO[]> => {
  const response = await adminApi.get(`/api/admin/assessment-rules/topic/${topicId}`);
  return response.data.data;
};

export const createRule = async (rule: TopicAssessmentRuleDTO): Promise<TopicAssessmentRuleDTO> => {
  const response = await adminApi.post('/api/admin/assessment-rules', rule);
  return response.data.data;
};

export const deleteRule = async (ruleId: number): Promise<void> => {
  await adminApi.delete(`/api/admin/assessment-rules/${ruleId}`);
};

export const generateQuizFromRule = async (ruleId: number, subjectId?: number): Promise<QuizDTO> => {
  const response = await adminApi.post(`/api/admin/assessment-rules/${ruleId}/generate?subjectId=${subjectId || ''}`);
  return response.data.data;
};

// ========== Topic Content APIs ==========

export interface LLMContentRequest {
  topicTitle: string;
  context?: string;
  contentType?: string;
  difficultyLevel?: string;
  approxDurationMinutes?: number;
}

export const generateTopicContent = async (
  subjectId: number,
  topicId: number,
  request: LLMContentRequest
): Promise<string> => {
  const response = await adminApi.post(
    `/api/admin/graph/topics/subject/${subjectId}/topics/${topicId}/content`,
    request
  );
  return response.data.data;
};

/** Get topic body content from TopicContentController (topic_content table). Returns null if 404. */
export const getTopicContent = async (topicId: number): Promise<string | null> => {
  try {
    const response = await adminApi.get<{ data?: { content?: string } }>(`/api/admin/topics/${topicId}/content`);
    return response.data?.data?.content ?? null;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

export const updateTopicContent = async (
  topicId: number,
  content: string
): Promise<void> => {
  // Topic content is stored via TopicContentController (not graph); PUT /api/admin/topics/{topicId}/content
  await adminApi.put(`/api/admin/topics/${topicId}/content`, { content });
};

// ========== Course Structure Graph APIs (Redesign) ==========

export interface CourseStructureNodeDTO {
  id: number;
  courseId: number;
  viewId?: number;
  parentId?: number;
  nodeType: 'WRAPPER' | 'CURRICULUM_REF' | 'TOPIC_REF';
  refId?: number;
  label?: string;
  displayOrder?: number;
  isVisible?: boolean;
  isExpanded?: boolean;
  children?: CourseStructureNodeDTO[];

  // UI Helpers
  refTitle?: string;
  refDescription?: string;
  deleteEnabled?: boolean;
  editEnabled?: boolean;
}


export interface CourseViewVersionDTO {
  id: number;
  courseViewId: number;
  versionNumber: string;
  name?: string;
  createdAt: string;
  createdBy?: number;
}

export const getCourseStructure = async (
  courseId: number,
  viewId?: number,
  versionId?: number
): Promise<CourseStructureNodeDTO[]> => {
  let url = `/api/admin/graph/courses/${courseId}/structure`;
  const params = new URLSearchParams();
  if (viewId) params.append('viewId', viewId.toString());
  if (versionId) params.append('versionId', versionId.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await adminApi.get(url);
  return response.data;
};

export const getStudentCourseStructure = async (
  courseId: number,
  viewId?: number
): Promise<CourseStructureNodeDTO[]> => {
  let url = `/api/admin/graph/courses/${courseId}/student-structure`;
  if (viewId) {
    url += `?viewId=${viewId}`;
  }
  const response = await adminApi.get(url);
  return response.data;
};

export const getCourseStructureVersions = async (
  courseId: number,
  viewId?: number
): Promise<CourseViewVersionDTO[]> => {
  const response = await adminApi.get(
    `/api/admin/graph/courses/${courseId}/structure/versions${viewId ? `?viewId=${viewId}` : ''}`
  );
  return response.data;
};

export const publishCourseStructure = async (
  courseId: number,
  viewId?: number
): Promise<CourseViewVersionDTO> => {
  const response = await adminApi.post(
    `/api/admin/graph/courses/${courseId}/structure/publish${viewId ? `?viewId=${viewId}` : ''}`
  );
  return response.data;
};

export const restoreCourseStructureVersion = async (
  courseId: number,
  versionId: number,
  viewId?: number
): Promise<void> => {
  await adminApi.post(
    `/api/admin/graph/courses/${courseId}/structure/versions/${versionId}/restore${viewId ? `?viewId=${viewId}` : ''}`
  );
};

export const deleteCourseStructure = async (
  courseId: number,
  viewId?: number,
  versionId?: number
): Promise<void> => {
  let url = `/api/admin/graph/courses/${courseId}/structure`;
  const params = new URLSearchParams();
  if (viewId) params.append('viewId', viewId.toString());
  if (versionId) params.append('versionId', versionId.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  await adminApi.delete(url);
};

export const deleteCourseStructureVersion = async (
  courseId: number,
  versionId: number,
  viewId?: number
): Promise<void> => {
  await adminApi.delete(
    `/api/admin/graph/courses/${courseId}/structure/versions/${versionId}${viewId ? `?viewId=${viewId}` : ''}`
  );
};

export const saveCourseStructure = async (
  courseId: number,
  nodes: CourseStructureNodeDTO[],
  viewId?: number
): Promise<void> => {
  await adminApi.post(
    `/api/admin/graph/courses/${courseId}/structure/save${viewId ? `?viewId=${viewId}` : ''}`,
    nodes
  );
};

export const updateCourseStructureNode = async (
  nodeId: number,
  updates: { isVisible?: boolean; isExpanded?: boolean; label?: string }
): Promise<void> => {
  await adminApi.put(`/api/admin/graph/courses/nodes/${nodeId}`, updates);
};

export const moveCourseStructureNode = async (
  courseId: number,
  nodeId: number,
  updates: { newParentId?: number; newIndex?: number }
): Promise<void> => {
  await adminApi.post(`/api/admin/graph/courses/${courseId}/structure/nodes/${nodeId}/move`, updates);
};

export const deleteCourseStructureNode = async (
  courseId: number,
  nodeId: number
): Promise<void> => {
  await adminApi.delete(`/api/admin/graph/courses/${courseId}/structure/nodes/${nodeId}`);
};

/**
 * Create a course-local custom topic (not linked to core subject).
 */
export const createCourseCustomTopic = async (
  courseId: number,
  topic: { title: string; description?: string }
): Promise<CoreTopicDTO> => {
  const response = await adminApi.post(`/api/admin/courses/${courseId}/custom-topics`, topic);
  return response.data.data;
};

/**
 * List course-local custom topics (topics with course_id set).
 */
export const getCourseCustomTopics = async (courseId: number): Promise<CoreTopicDTO[]> => {
  const response = await adminApi.get(`/api/admin/courses/${courseId}/custom-topics`);
  return response.data.data;
};

export const addCourseStructureNode = async (
  courseId: number,
  viewId: number | undefined,
  node: { parentId?: number; nodeType: string; refId?: number; label?: string }
): Promise<CourseStructureNodeDTO> => {
  let url = `/api/admin/graph/courses/${courseId}/structure/nodes`;
  if (viewId) url += `?viewId=${viewId}`;

  const response = await adminApi.post(url, node);
  return response.data;
};

// ========== Course lifecycle actions ==========

/**
 * Activate a course (validates ≥ 1 topic assigned, sets status = ACTIVE).
 */
export const activateCourse = async (courseId: number): Promise<CourseDTO> => {
  const response = await adminApi.post(`/api/admin/courses/${courseId}/activate`);
  return response.data.data;
};

/**
 * Archive a course (sets status = ARCHIVED).
 */
export const archiveCourse = async (courseId: number): Promise<CourseDTO> => {
  const response = await adminApi.post(`/api/admin/courses/${courseId}/archive`);
  return response.data.data;
};

// ========== Topic picker ==========

export interface AdminTopicDTO {
  id: number;
  title: string;
  description?: string;
  estimatedTimeMinutes?: number;
  difficultyLevel?: string;
  displayOrder?: number;
  status?: string;
  parentTopicId?: number;
  coreSubjectId?: number;
}

/**
 * Get topics from the topics table filtered by core_subject_id.
 * Used by the course topic picker.
 */
export const getTopicsByCoreSubject = async (
  coreSubjectId: number,
  status = 'ACTIVE'
): Promise<AdminTopicDTO[]> => {
  const response = await adminApi.get('/api/admin/topics', {
    params: { coreSubjectId, status },
  });
  return response.data.data;
};

